import { GridColDef } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import { DataTableColumn } from 'components/tables/data-table';
import dayjs from 'dayjs';
import { default as tz } from 'dayjs/plugin/timezone';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, PropsWithChildren, createContext, useContext, useState } from 'react';
import { Contract } from 'types';

type ContractListContextType = {
  columnDefs: GridColDef<Contract>[];
  columnVisibilityModel: { [P in keyof Partial<Contract>]: boolean };
  saveContractList: (state: GridInitialStateCommunity) => void;
  contractListState?: GridInitialStateCommunity;
};

dayjs.extend(utc);
dayjs.extend(tz);

const DollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const columnDefs: GridColDef<Contract>[] = [
  {
    width: 125,
    field: 'id',
    headerName: 'Contract #',
    type: 'number',
    align: 'left'
  },
  {
    width: 275,
    field: 'customer',
    headerName: 'Customer',
    valueGetter: ({ value: customer }) => customer?.name
  },
  {
    width: 175,
    field: 'startDate',
    type: 'date',
    headerName: 'Start Date',
    valueGetter: ({ value }) => (!value ? null : new Date(value)),
    valueFormatter: ({ value }) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
  },
  {
    width: 140,
    field: 'endDate',
    type: 'date',
    headerName: 'End Date',
    valueGetter: ({ value }) => (!value ? null : new Date(value)),
    valueFormatter: ({ value }) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
  },
  {
    width: 100,
    field: 'active',
    headerName: 'Active',
    type: 'boolean'
  },
  {
    width: 80,
    field: 'unitSizeType',
    headerName: 'Size'
  },
  {
    width: 175,
    field: 'dailyEmptyContainer',
    headerName: 'Container Only',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 155,
    field: 'dailyChassis',
    headerName: 'Chassis Only',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 100,
    field: 'dailyEmpty',
    headerName: 'Setup',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 125,
    field: 'liftOffEmpty',
    headerName: 'Lift Off',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 125,
    field: 'liftOnEmpty',
    headerName: 'Lift On',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 80,
    field: 'digEmpty',
    headerName: 'Dig',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 125,
    field: 'gateInEmpty',
    headerName: 'Gate In',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 190,
    field: 'gateInEmptyContainer',
    headerName: 'Container Gate In',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 175,
    field: 'gateInChassis',
    headerName: 'Chassis Gate In',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 125,
    field: 'gateOutEmpty',
    headerName: 'Gate Out',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 210,
    field: 'gateOutEmptyContainer',
    headerName: 'Container Gate Out',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 190,
    field: 'gateOutChassis',
    headerName: 'Chassis Gate Out',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 130,
    field: 'freeDays',
    headerName: 'Free Days',
    type: 'number'
  },
  {
    width: 200,
    field: 'freeDaysLoaded',
    headerName: 'Loaded Free Days',
    type: 'number'
  },
  {
    width: 175,
    field: 'dailyLoaded',
    headerName: 'Loaded Rate',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 175,
    field: 'liftOffLoaded',
    headerName: 'Loaded Lift Off',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 175,
    field: 'liftOnLoaded',
    headerName: 'Loaded Lift On',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 150,
    field: 'digLoaded',
    headerName: 'Loaded Dig',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 175,
    field: 'gateInLoaded',
    headerName: 'Loaded Gate In',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  },
  {
    width: 185,
    field: 'gateOutLoaded',
    headerName: 'Loaded Gate Out',
    valueFormatter: ({ value }) => DollarFormatter.format(value),
    type: 'number'
  }
];

export const contractDataColumns: DataTableColumn[] = [
  {
    width: 125,
    category: 'common',
    field: 'id',
    label: 'Contract #',
    type: 'number',
    className: 'link font-weight-regular'
  },
  {
    width: 275,
    category: 'common',
    field: 'customer',
    label: 'Customer',
    getValue: (row: Contract) => row.customer?.name
  },
  {
    width: 120,
    category: 'common',
    field: 'customerCode',
    label: 'Customer Code',
    getValue: (row: Contract) => row.customer?.code,
    sortable: false,
    filterable: false
  },
  {
    width: 175,
    category: 'common',
    field: 'startDate',
    type: 'date',
    label: 'Start Date',
    getValue: (row: Contract) => (!row.startDate ? null : new Date(row.startDate)),
    getValueLabel: (v: Date, row?: Contract) =>
      !!v
        ? dayjs(v)
            .tz(row?.location?.timeZone || 'UTC')
            .format('M/D/YYYY')
        : null
  },
  {
    width: 140,
    category: 'common',
    field: 'endDate',
    type: 'date',
    label: 'End Date',
    getValue: (row: Contract) => (!row.endDate ? null : new Date(row.endDate)),
    getValueLabel: (v: Date, row?: Contract) =>
      !!v
        ? dayjs(v)
            .tz(row?.location?.timeZone || 'UTC')
            .format('M/D/YYYY')
        : null
  },
  {
    width: 100,
    category: 'common',
    field: 'active',
    label: 'Active',
    type: 'boolean'
  },
  {
    width: 80,
    category: 'common',
    field: 'unitSizeType',
    label: 'Size'
  },
  {
    width: 175,
    category: 'common',
    field: 'dailyEmptyContainer',
    label: 'Container Only',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 155,
    category: 'common',
    field: 'dailyChassis',
    label: 'Chassis Only',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 100,
    category: 'common',
    field: 'dailyEmpty',
    label: 'Setup',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 125,
    category: 'common',
    field: 'liftOffEmpty',
    label: 'Lift Off',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 125,
    category: 'common',
    field: 'liftOnEmpty',
    label: 'Lift On',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 80,
    category: 'common',
    field: 'digEmpty',
    label: 'Dig',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 125,
    category: 'common',
    field: 'gateInEmpty',
    label: 'Gate In',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 190,
    category: 'common',
    field: 'gateInEmptyContainer',
    label: 'Container Gate In',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 175,
    category: 'common',
    field: 'gateInChassis',
    label: 'Chassis Gate In',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 125,
    category: 'common',
    field: 'gateOutEmpty',
    label: 'Gate Out',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 210,
    category: 'common',
    field: 'gateOutEmptyContainer',
    label: 'Container Gate Out',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 190,
    category: 'common',
    field: 'gateOutChassis',
    label: 'Chassis Gate Out',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 130,
    category: 'common',
    field: 'freeDays',
    label: 'Free Days',
    type: 'number'
  },
  {
    width: 200,
    category: 'common',
    field: 'freeDaysLoaded',
    label: 'Loaded Free Days',
    type: 'number'
  },
  {
    width: 175,
    category: 'common',
    field: 'dailyLoaded',
    label: 'Loaded Rate',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 175,
    category: 'common',
    field: 'liftOffLoaded',
    label: 'Loaded Lift Off',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 175,
    category: 'common',
    field: 'liftOnLoaded',
    label: 'Loaded Lift On',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 150,
    category: 'common',
    field: 'digLoaded',
    label: 'Loaded Dig',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 175,
    category: 'common',
    field: 'gateInLoaded',
    label: 'Loaded Gate In',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  },
  {
    width: 185,
    category: 'common',
    field: 'gateOutLoaded',
    label: 'Loaded Gate Out',
    getValueLabel: (v: number) => DollarFormatter.format(v),
    type: 'number'
  }
];

const columnVisibilityModel: { [P in keyof Partial<Contract>]: boolean } = {
  id: false,
  customer: true,
  startDate: true,
  active: true,
  unitSizeType: true,
  dailyEmptyContainer: true,
  dailyChassis: true,
  dailyEmpty: true,
  liftOffEmpty: true,
  liftOnEmpty: true,
  digEmpty: true,
  gateInEmpty: true,
  gateInEmptyContainer: true,
  gateInChassis: true,
  gateOutEmpty: true,
  gateOutEmptyContainer: true,
  gateOutChassis: true,
  freeDays: true,
  freeDaysLoaded: true,
  dailyLoaded: true,
  liftOffLoaded: true,
  liftOnLoaded: true,
  digLoaded: true,
  gateInLoaded: true,
  gateOutLoaded: true
};

const ContractListContext = createContext<ContractListContextType>({
  columnDefs: [] as GridColDef<Contract>[],
  columnVisibilityModel,
  saveContractList: () => {}
});

export const useContractList = () => useContext(ContractListContext);

export const ContractListProvider: FC<PropsWithChildren> = ({ children }) => {
  const [contractListState, saveContractList] = useState<GridInitialStateCommunity>();

  return (
    <ContractListContext.Provider
      value={{
        columnDefs,
        columnVisibilityModel,
        contractListState,
        saveContractList
      }}
    >
      {children}
    </ContractListContext.Provider>
  );
};
